import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaExternalLinkAlt, FaFileDownload } from 'react-icons/fa'

import { getAttachmentPreviewKind } from '../../utils/attachmentPreview.utils'
import { AttachmentPreviewContent, AttachmentTypeIcon } from './AttachmentPreview'

const AttachmentsExplorer = ({ files }) => {
  const { t } = useTranslation()
  const [activeFileId, setActiveFileId] = useState(files[0]?.attachment_id ?? null)

  const showSidebar = files.length > 1

  useEffect(() => {
    if (!files.length) {
      setActiveFileId(null)
      return
    }

    const stillExists = files.some((f) => f.attachment_id === activeFileId)
    if (!stillExists) {
      setActiveFileId(files[0].attachment_id)
    }
  }, [files, activeFileId])

  const activeFile = files.find((f) => f.attachment_id === activeFileId) ?? files[0]
  if (!activeFile) return null

  const previewKind = getAttachmentPreviewKind(activeFile)
  const fileUrl = activeFile.attachment_url

  return (
    <div className="flex h-[calc(100vh-14rem)] max-h-[calc(100vh-14rem)] overflow-hidden rounded-lg border border-slate-300 bg-white shadow-sm">
      {showSidebar && (
        <aside className="flex w-56 shrink-0 flex-col border-r border-slate-200 bg-slate-50">
          <p className="shrink-0 border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('support.detail.files_list', { count: files.length })}
          </p>
          <ul className="min-h-0 flex-1 overflow-y-auto py-1">
            {files.map((file) => {
              const kind = getAttachmentPreviewKind(file)
              const isActive = file.attachment_id === activeFile.attachment_id

              return (
                <li key={file.attachment_id}>
                  <button
                    type="button"
                    onClick={() => setActiveFileId(file.attachment_id)}
                    title={file.original_filename}
                    className={`flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors ${
                      isActive
                        ? 'border-l-2 border-sky-600 bg-sky-50 pl-[10px] text-sky-900'
                        : 'border-l-2 border-transparent text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <AttachmentTypeIcon kind={kind} />
                    <span className="line-clamp-2 min-w-0 flex-1 text-xs leading-snug">{file.original_filename}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </aside>
      )}

      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <AttachmentTypeIcon kind={previewKind} />
            <p className="truncate text-sm font-medium text-slate-800" title={activeFile.original_filename}>
              {activeFile.original_filename}
            </p>
          </div>
          {fileUrl ? (
            <div className="flex shrink-0 items-center gap-2">
              <a
                href={fileUrl}
                download={activeFile.original_filename}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <FaFileDownload className="text-sky-600" />
                {t('support.detail.download_file')}
              </a>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <FaExternalLinkAlt className="text-sky-600" />
                {t('support.detail.open_in_new_tab')}
              </a>
            </div>
          ) : null}
        </header>

        <div className="min-h-0 flex-1 overflow-hidden bg-slate-100">
          <AttachmentPreviewContent file={activeFile} />
        </div>
      </section>
    </div>
  )
}

export default AttachmentsExplorer
